export const toolNameMap: Record<string, string> = {
  knowledgeBase: "Knowledge Base",
  currentMedications: "Current Medications",
  medicationInfoForNdc: "Medication Info",
  basicDemographics: "Demographics",
  messages: "Messages",
  userTasks: "Tasks",
  observations: "Observations",
  labOrders: "Lab Orders",
  labOrderResults: "Lab Order Results",
  prescriptions: "Prescriptions",
  notes: "Notes",
  appointments: "Appointments",
  consultations: "Consultations",
  medicalConditions: "Medical Conditions",
  formularySheetInsuranceCoverage: "Insurance Coverage"
};

export const humanizeToolName = (name: string): string =>
  toolNameMap[name] ??
  name.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase());

export const formatToolArgs = (
  args: Record<string, unknown> | undefined
): string => {
  if (!args) {
    return "";
  }
  const entries = Object.entries(args);
  if (entries.length === 0) {
    return "";
  }
  return entries.map(([k, v]) => `${k}: ${JSON.stringify(v)}`).join(", ");
};

export interface ParsedToolResult {
  /** The result as it should be displayed. */
  text: string;
  /** True when the tool returned prose rather than a JSON structure. */
  isProse: boolean;
}

/**
 * Spring AI serializes a tool's return value to JSON, so a tool that returns a
 * String arrives here double-encoded (`"line one\nline two"` — quotes and
 * escapes included). Parsing that to a string and re-stringifying it would
 * escape it a second time, which is what made knowledge base responses
 * unreadable. Only objects and arrays are worth pretty-printing; a string is
 * already its own best rendering.
 */
export const parseToolResult = (raw: string): ParsedToolResult => {
  try {
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === "string"
      ? { text: parsed, isProse: true }
      : { text: JSON.stringify(parsed, null, 2), isProse: false };
  } catch (error) {
    if (!(error instanceof SyntaxError)) {
      throw error;
    }
    return { text: raw, isProse: true };
  }
};
